import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  ComponentRef,
  Directive,
  ElementRef,
  OnDestroy,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { ClassValue } from '../../utils/merge-classes';
import { TooltipComponent } from './tooltip.component';

export type TooltipPosition = 'above' | 'below';
export type TooltipAlign = 'start' | 'center' | 'end';

const TOOLTIP_OFFSET_PX = 8;
const VIEWPORT_MARGIN_PX = 8;

let nextTooltipId = 0;

@Directive({
  selector: '[appTooltip]',
  standalone: true,
  host: {
    '(mouseenter)': 'show()',
    '(mouseleave)': 'hide()',
    '(focusin)': 'show()',
    '(focusout)': 'hide()',
    '(keydown.escape)': 'hide()',
    '[attr.aria-describedby]': 'describedBy()',
  },
})
export class TooltipDirective implements OnDestroy {
  readonly text = input<string | null | undefined>(null, { alias: 'appTooltip' });
  readonly position = input<TooltipPosition>('below', { alias: 'appTooltipPosition' });
  readonly align = input<TooltipAlign>('center', { alias: 'appTooltipAlign' });
  readonly tooltipClass = input<ClassValue>(null, { alias: 'appTooltipClass' });

  private readonly overlay = inject(Overlay);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly id = `app-tooltip-${nextTooltipId++}`;
  private readonly visible = signal(false);
  private overlayRef: OverlayRef | null = null;
  private tooltipRef: ComponentRef<TooltipComponent> | null = null;

  protected readonly describedBy = computed(() => (this.visible() ? this.id : null));

  constructor() {
    effect(() => {
      const text = this.text();
      const tooltipClass = this.tooltipClass();
      untracked(() => {
        if (!text) {
          this.hide();
          return;
        }
        this.tooltipRef?.setInput('text', text);
        this.tooltipRef?.setInput('tooltipClass', tooltipClass);
      });
    });
  }

  show(): void {
    const text = this.text();
    if (!text || this.overlayRef) return;

    this.overlayRef = this.overlay.create({
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.elementRef)
        .withPositions(this.positions())
        .withFlexibleDimensions(false)
        .withViewportMargin(VIEWPORT_MARGIN_PX),
      scrollStrategy: this.overlay.scrollStrategies.close(),
    });

    this.tooltipRef = this.overlayRef.attach(new ComponentPortal(TooltipComponent));
    this.tooltipRef.setInput('id', this.id);
    this.tooltipRef.setInput('text', text);
    this.tooltipRef.setInput('tooltipClass', this.tooltipClass());
    this.visible.set(true);
  }

  hide(): void {
    this.overlayRef?.dispose();
    this.overlayRef = null;
    this.tooltipRef = null;
    this.visible.set(false);
  }

  ngOnDestroy(): void {
    this.hide();
  }

  private positions(): ConnectedPosition[] {
    const x = this.align();
    const above: ConnectedPosition = {
      originX: x,
      originY: 'top',
      overlayX: x,
      overlayY: 'bottom',
      offsetY: -TOOLTIP_OFFSET_PX,
    };
    const below: ConnectedPosition = {
      originX: x,
      originY: 'bottom',
      overlayX: x,
      overlayY: 'top',
      offsetY: TOOLTIP_OFFSET_PX,
    };
    return this.position() === 'above' ? [above, below] : [below, above];
  }
}
